import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { ArrowRight, MessageSquare, type LucideIcon } from "lucide-react"

export type StudioCardTone = "warning" | "success" | "default"

export interface StudioCardStat {
  value: string
  caption: string
  tone: StudioCardTone
}

/** "loading" → skeleton, "unavailable" → em dash (request failed). */
export type StudioCardStatState = StudioCardStat | "loading" | "unavailable"

export interface StudioCardBackRow {
  label: string
  /** null renders an em dash (data unavailable). */
  value: string | null
}

export interface StudioCardProps {
  icon: LucideIcon
  title: string
  question: string
  stat: StudioCardStatState
  /** Metric label kept visible in the unavailable state under the em dash. */
  statCaption: string
  roleLabel: string
  badge?: { count: number; tooltip: string }
  backTitle: string
  backRows: StudioCardBackRow[]
  /** Rendered after backRows, behind a divider. Nothing renders when absent or empty. */
  backSubsection?: { title: string; rows: StudioCardBackRow[] }
  ctaLabel: string
  onOpen: () => void
  /** For a page that shows more cards than the start page's three. */
  className?: string
  /** Sets the question's line box, for a grid whose cards must line up. */
  questionClassName?: string
}

const statToneClass: Record<StudioCardTone, string> = {
  warning: "text-warning",
  success: "text-success",
  default: "text-white",
}

/**
 * Studio entry card for the MC start page. The front face answers "where do
 * I work?", hover or keyboard focus slides in the back face with detail rows
 * and a CTA affordance. The whole card is one native <button> (module idiom,
 * see HorizontalCard) — the back-face "CTA" is visual affordance only.
 */
export function StudioCard({
  icon: Icon,
  title,
  question,
  stat,
  statCaption,
  roleLabel,
  badge,
  backTitle,
  backRows,
  backSubsection,
  ctaLabel,
  onOpen,
  className,
  questionClassName,
}: StudioCardProps) {
  const face =
    "absolute inset-0 flex flex-col bg-grey2 p-6 transition-transform duration-300 ease-out motion-reduce:transition-none"

  return (
    <button
      type="button"
      aria-label={
        typeof stat === "string"
          ? title
          : `${title}: ${stat.value} ${stat.caption}`
      }
      onClick={onOpen}
      className={cn(
        "group relative h-[380px] cursor-pointer overflow-hidden rounded-xl border border-grey4 bg-grey2 text-left outline-hidden transition-colors hover:border-grey5 focus-visible:ring-1 focus-visible:ring-ring lg:h-[460px]",
        className,
      )}
    >
      {badge && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="absolute right-3.5 top-3.5 z-10 inline-flex h-6 items-center gap-1.5 rounded-full border border-warning/30 bg-warning/10 px-2.5 text-xs font-semibold text-warning">
                <MessageSquare size={12} />
                {badge.count}
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              className="max-w-[230px] border-grey4 bg-grey1 text-white"
            >
              {badge.tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* front face */}
      <div
        className={cn(
          face,
          "group-hover:-translate-y-full group-focus-visible:-translate-y-full",
        )}
      >
        <span className="mx-auto mt-auto text-grey5 opacity-55">
          <Icon size={48} strokeWidth={1.25} />
        </span>
        <div className="mt-5 text-center text-[21px] font-semibold leading-tight tracking-tight text-white">
          {title}
        </div>
        <div
          className={cn(
            "mt-2 px-1.5 text-center text-xs leading-relaxed text-grey5",
            questionClassName,
          )}
        >
          {question}
        </div>
        <div className="mt-auto pt-4 text-center">
          {stat === "loading" ? (
            <div
              className="mx-auto h-12 w-36 animate-pulse rounded-md bg-grey4"
              data-testid="stat-skeleton"
            />
          ) : stat === "unavailable" ? (
            <div data-testid="stat-unavailable">
              <div className="text-[30px] font-medium leading-tight text-grey5">
                —
              </div>
              <div className="mt-1.5 text-xs text-grey5">{statCaption}</div>
            </div>
          ) : (
            <>
              <div
                className={cn(
                  "text-[30px] font-medium tabular-nums leading-tight tracking-tight",
                  statToneClass[stat.tone],
                )}
              >
                {stat.value}
              </div>
              <div className="mt-1.5 text-xs text-grey5">{stat.caption}</div>
            </>
          )}
        </div>
        <div className="font-mono mt-4 border-t border-white/5 pt-3 text-center text-[10px] uppercase tracking-[0.18em] text-grey5">
          {roleLabel}
        </div>
      </div>

      {/* back face */}
      <div
        className={cn(
          face,
          "translate-y-full group-hover:translate-y-0 group-focus-visible:translate-y-0",
        )}
      >
        <div className="font-mono border-b border-grey4 pb-3 text-center text-[10px] uppercase tracking-[0.16em] text-grey5">
          {backTitle}
        </div>
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-0.5 scrollbar-thin">
          <div>
            {backRows.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-2.5 border-t border-white/5 px-0.5 py-1.5 text-[13px] leading-5 text-grey5 first:border-t-0"
              >
                <span>{row.label}</span>
                <span className="font-semibold tabular-nums text-white">
                  {row.value ?? "—"}
                </span>
              </div>
            ))}
          </div>
          {backSubsection && backSubsection.rows.length > 0 && (
            <div className="mt-3 border-t border-grey4 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-grey5">
                {backSubsection.title}
              </div>
              <div className="mt-3">
                {backSubsection.rows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between gap-2.5 border-t border-white/5 px-0.5 py-1.5 text-[13px] leading-5 text-grey5 first:border-t-0"
                  >
                    <span>{row.label}</span>
                    <span className="font-semibold tabular-nums text-white">
                      {row.value ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <span className="mt-3 flex h-[38px] w-full shrink-0 items-center justify-center gap-2 rounded-md bg-yellow1 text-sm font-bold text-black">
          {ctaLabel}
          <ArrowRight size={15} />
        </span>
      </div>
    </button>
  )
}
